import Button, { type ButtonProps } from "@common/controls/Button/component";
import Icon from "@common/controls/Icon/component";
import Portal from "@common/controls/Portal/component";
import ResponsiveContainer from "@common/controls/ResponsiveContainer/component";
import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { usePromise } from "@common/lib/hooks";
import * as React from "react";
import { MediaImageComponent } from "../Media/component";
import { type Courses } from "./types";

import "./style.less";

export type Props = {
  ButtonComponent?: React.ComponentType<ButtonProps>;
  courseImageStyle?: React.CSSProperties;
  courses: Courses.Course[];
  courseStyle?: React.CSSProperties;
  getIframeSrc: (course: Courses.Course) => Promise<string>;
  isEnrolled: boolean;
  isTitleHidden?: boolean;
  onCourseClick: (course: Courses.Course) => void;
  onMembershipCourseExit: (args: any) => void;
  renderCourseAddon?: (course: Courses.Course) => any;
};

export default (props: Props) => {
  const i18n = useI18n();

  const [membershipCourse, setMembershipCourse] = React.useState<Courses.Course | null>(null);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "WSC_EXIT") {
        props.onMembershipCourseExit(event.data.args);

        setMembershipCourse(null);
        setIframeSrc(null);
      }
    };

    if (membershipCourse) {
      window.addEventListener("message", onMessage);
    }

    return () => {
      membershipCourse && window.removeEventListener("message", onMessage);
    };
  }, [props.onMembershipCourseExit, membershipCourse]);

  const onCourseClick = React.useCallback(
    (course: Courses.Course) => {
      if (course.isMembership || !course.isEnrolled) {
        setMembershipCourse(course);
      } else {
        props.onCourseClick(course);
      }
    },
    [props.onCourseClick, setMembershipCourse]
  );

  const ButtonComponent = props.ButtonComponent ?? Button;

  const [iframeSrc, _, setIframeSrc] = usePromise<string | null>({
    fn: props.getIframeSrc,
    args: [membershipCourse],
    shouldLoad: !!membershipCourse
  });

  return (
    <div className="course_list">
      {!props.isTitleHidden && <p className="title">{props.isEnrolled ? i18n.myLearning : i18n.enroll}:</p>}

      <div className="courses">
        {props.courses.map((c) => {
          return (
            <div key={c.id} className="item" style={props.courseStyle}>
              {c.mediaSourceId ? (
                <MediaImageComponent
                  aspectRatio="16/9"
                  containerClassName="image_wrap"
                  containerStyle={props.courseImageStyle}
                  sourceKey={"URL"}
                  sourceId={c.mediaSourceId}
                />
              ) : (
                <ResponsiveContainer className="image_wrap" aspectRatio="16/9" style={props.courseImageStyle}>
                  <Icon className="no_image" icon={IconType.Image} />
                </ResponsiveContainer>
              )}

              <div className="content">
                <span className="name">{c.name}</span>

                <div className="gap" />
                {props.renderCourseAddon && props.renderCourseAddon(c)}
                <ButtonComponent isDisabled={membershipCourse === c} onClick={() => onCourseClick(c)}>
                  {membershipCourse === c ? `${i18n.opening}...` : props.isEnrolled ? i18n.open : i18n.enroll}
                </ButtonComponent>
              </div>
            </div>
          );
        })}
      </div>

      {!!iframeSrc && (
        <Portal>
          <iframe className="fullscreen" src={iframeSrc} />
        </Portal>
      )}
    </div>
  );
};
